import {Tooltip} from '@mantine/core';
import Image from 'next/image';
import {useState} from 'react';
import type {z} from 'zod';
import type {CompaniesSchema} from '../../../../../core/entities/usersMe/schema';
import {cn} from '../../../../../lib/classname';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {buildCompanyFollowGtmContent} from '../../../../../shared/services/buildCompanyFollowGtmContent';
import {useMediaQuery} from '../../../../../shared/services/useMediaQuery';
import {FollowCompanyButton} from '../FollowCompanyButton';

type Props = {
  company: z.infer<typeof CompaniesSchema>;
  sizeClassName?: string;
  componentId?: string;
};

export const CompanyCardPickup: React.FC<Props> = ({
  company,
  sizeClassName = 'w-11/12 pc:w-[369px]',
  componentId,
}) => {
  const [showImage, setShowImage] = useState(true);
  const [showLogo, setShowLogo] = useState(true);
  const companyPagePath = `/company/${company.id}`;
  const {isPC} = useMediaQuery();
  return (
    <div className={cn('cursor-pointer hover:opacity-80', sizeClassName)}>
      <CommonLink href={companyPagePath} data-gtm-click={componentId}>
        <div
          className={
            'bg-black-4 pc:w-[369px] pc:h-[154px] rounded-16 shadow-card overflow-hidden'
          }
        >
          {showImage ? (
            <Image
              className="rounded-16 overflow-hidden line-clamp-1"
              src={company.backgroundImageURLs['640x268']}
              width={369}
              height={isPC ? 154 : 143}
              alt={`${company.name}のメイン写真`}
              onError={() => {
                setShowImage(false);
              }}
            />
          ) : (
            <div
              style={{
                height: `${isPC ? 154 : 143}px`,
              }}
            />
          )}
        </div>
      </CommonLink>
      <div className="flex justify-between">
        <CommonLink href={companyPagePath} data-gtm-click={componentId}>
          {showLogo && (
            <div className="mt-[-16px] relative z-10 ml-8">
              <div className="bg-white p-4 rounded-4 shadow-card">
                <Image
                  src={company.logoURLs['140x105']}
                  className="overflow-hidden line-clamp-1"
                  width={40}
                  height={40}
                  alt={`${company.name}のロゴ画像`}
                  onError={() => {
                    setShowLogo(false);
                  }}
                />
              </div>
            </div>
          )}
        </CommonLink>
        <div className="flex mt-8">
          <FollowCompanyButton
            companyId={company.id}
            companyName={company.name}
            followCount={company.followerCount}
            isFollowed={company.isFollowed}
            dataGtmContent={buildCompanyFollowGtmContent({
              companyId: company.id,
              companyName: company.name,
              location: 'company-list-pickup',
            })}
          />
        </div>
      </div>
      <CommonLink href={companyPagePath} data-gtm-click={componentId}>
        <div className="mt-8 line-clamp-2 text-md font-bold h-[42px]">
          {company.name}
        </div>
        <div className="w-[369px] flex gap-8 text-black-3 text-sm py-4 items-center h-36">
          {company.isHiring && (
            <div className="rounded-16 bg-primary text-white px-8 py-4 text-nowrap">
              募集あり
            </div>
          )}
          {company.workAttractiveConditions?.[0] && (
            <div
              key={company.workAttractiveConditions[0].id}
              className="flex justify-center items-center gap-4 py-4 h-full"
            >
              <div
                className="rounded-16 text-primary border border-primary px-8 line-clamp-1"
                style={{
                  lineHeight: '24px',
                }}
              >
                {company.workAttractiveConditions[0].name}
              </div>
              {company.workAttractiveConditions.length > 1 && (
                <div className="text-black-2 text-sm text-nowrap">
                  {`ほか${company.workAttractiveConditions.length - 1}つ`}
                </div>
              )}
            </div>
          )}
        </div>
        <Tooltip label={company.catchPhrase} disabled={!company.catchPhrase}>
          <div className="text-md line-clamp-1">{company.catchPhrase}</div>
        </Tooltip>
      </CommonLink>
    </div>
  );
};
